import { escapeHtml } from "./RiskAssessment/RiskFormat";

const METRIC_HELP = {
  "Risk score": "Overall assessed route risk from 1 to 100; higher means greater risk. Combines threat, vulnerability and impact, including the risk at mandatory stops. It is not a percentage probability of an incident.",
  Confidence: "How well the available reporting data supports the assessment. Low confidence means greater uncertainty, even if the risk score is low.",
  "Incident density": "Reported incidents weighted by relevance, recency and severity, per 100 km of route. This is a weighted indicator, not a simple incident count.",
  "Safe parking coverage": "Share of the route covered by secure parking opportunities under the assessment spacing rule. Portions of parking gaps beyond that spacing count as uncovered. Higher coverage reduces vulnerability. The assessment uses its configured spacing, independently of the Resilience spacing slider.",
  "Driving exposure": "Motorway share and estimated night-driving share. The assessment uses road type and time of day to adjust threat exposure. Night exposure requires a departure time.",
  "Weakest mandatory-stop segment": "The highest-risk segment containing a required break or daily rest. Its score can raise the overall route score, so a risky stop is not hidden by safer driving sections.",
  "Dominant factor": "The factor with the largest weighted contribution to the route score: threat, vulnerability or impact.",
  Route: "Route distance, estimated driving duration and, when departure is set, estimated arrival including planned breaks and daily rests. Driving duration excludes those stops.",
  "Data window / corridor": "The actual incident date range and distance on each side of the route used for this assessment. Assessment includes all visible incidents in this window, independently of current map filters.",
  "Secure parkings": "Number of secure PSR parking sites within the selected route corridor and, for resilience routing, matching the selected security levels. This counts sites, not available parking spaces.",
  "Nearby incidents": "Number of incidents within the danger radius that match the resilience incident period and minimum loss value. The count is only calculated when incident avoidance is enabled; otherwise 0 does not mean no incidents exist.",
  "Parking coverage": "Parking coverage at the desired spacing: only the part of each gap beyond that spacing counts as uncovered. Includes gaps from the start and to the destination. Higher is better; this differs from reporting coverage in Risk Assessment.",
  "Max gap": "Largest distance along the route between secure parking opportunities, including the start-to-first-parking and last-parking-to-destination distances.",
  "Route difference": "Change in distance and estimated driving time compared with the standard reference route. Plus means longer; minus means shorter.",
  Segment: "Route section number. The circle marks a section containing a required break or daily rest. Select a row to see details.",
  km: "Start and end distance of this segment, measured from the route origin.",
  Time: "Estimated local start and end time of this segment, based on departure and including preceding breaks and daily rests.",
  Band: "Risk category corresponding to the score, from Low through Guarded, Elevated and High to Severe.",
  Score: "Assessed risk of this segment from 1 to 100. Higher means greater risk; this is not a percentage probability.",
  "Reporting coverage": "Data coverage indicator from 0 to 1, combining country reporting coverage and local evidence. Higher means stronger data support, not safer parking. Low coverage affects confidence and map styling, not the threat score.",
  Threat: "Risk signal from incident density, trend and concentration, adjusted for driving exposure. Higher values mean greater threat.",
  Vulnerability: "Exposure to unsafe stopping conditions, based on secure parking, required stops and other route conditions. Higher values mean greater vulnerability.",
  Impact: "Potential consequences based on cargo attractiveness and losses in reported incidents. Higher values mean greater impact.",
  Trend: "Direction and strength of change in monthly incident reporting over the selected period. Requires enough data to calculate.",
  "Hotspot concentration": "Share of weighted incidents concentrated in the busiest short section of the segment. Higher concentration indicates a more pronounced hotspot.",
  "Mandatory-stop safety": "Availability and security level of PSR parking within reach of the predicted required stops. A higher raw coverage share means safer stopping options.",
  "Overnight without PSR": "Number of predicted daily-rest stops without secure PSR parking within the assessment reach distance.",
  "Border crossings": "Number of country borders crossed in the segment, used as an indicator of possible disruption and stopping exposure.",
  "Unsecured parking share": "Share of relevant parking incidents reported at unclassified parking, among incidents at secured or unclassified parking. Records without a location type are excluded.",
  "Isolation / chokepoint": "Indicator for isolated or constrained route sections. If unavailable or assigned zero weight, it does not contribute to the score.",
  "Cargo attractiveness": "Impact indicator based on whether the planned cargo is marked as high value.",
  "Observed severity": "Loss severity derived from reported incident values. Higher values indicate more severe observed losses.",
  Value: "Original indicator value in the displayed unit. n/a means the indicator is unavailable or does not apply; its reason is shown where available.",
  "0–100": "Indicator converted to a common risk scale. Higher means greater risk, including for parking indicators where higher raw coverage means less risk.",
  Weight: "Configured importance of the indicator. The percentage after the arrow is its share after redistributing weight among applicable indicators.",
  "Contrib.": "Contribution to the factor score: the normalized indicator multiplied by its effective weight.",
};
METRIC_HELP["Secure parking coverage"] = METRIC_HELP["Safe parking coverage"];

export function metricHelp(label) {
  return METRIC_HELP[label] ? helpIcon(label, METRIC_HELP[label]) : "";
}

export function helpIcon(label, text) {
  return `<button type="button" class="route-help" aria-label="${escapeHtml(`Help: ${label}`)}" data-route-help="${escapeHtml(text)}"><i class="bi bi-info-circle" aria-hidden="true"></i></button>`;
}

// One floating tooltip also serves results inserted after a route is calculated.
export function initializeRouteHelp() {
  const root = document.querySelector(".map-index");
  if (!root) {
    return;
  }
  const tooltip = document.createElement("div");
  tooltip.id = "route-help-tooltip";
  tooltip.className = "route-help-tooltip";
  tooltip.setAttribute("role", "tooltip");
  tooltip.hidden = true;
  document.body.appendChild(tooltip);
  let active = null;
  let timer;

  const hide = () => {
    clearTimeout(timer);
    if (active) {
      active.removeAttribute("aria-describedby");
    }
    active = null;
    tooltip.hidden = true;
  };
  const show = (button) => {
    hide();
    active = button;
    tooltip.textContent = button.getAttribute("data-route-help");
    button.setAttribute("aria-describedby", tooltip.id);
    tooltip.hidden = false;
    const rect = button.getBoundingClientRect();
    const width = tooltip.offsetWidth;
    const height = tooltip.offsetHeight;
    tooltip.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`;
    tooltip.style.top = `${Math.max(8, rect.bottom + height + 8 <= window.innerHeight ? rect.bottom + 6 : rect.top - height - 6)}px`;
  };
  const scheduleHide = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (active && document.activeElement !== active && !active.matches(":hover") && !tooltip.matches(":hover")) {
        hide();
      }
    }, 150);
  };
  root.addEventListener("mouseover", (event) => {
    const button = event.target.closest(".route-help");
    if (button && button !== active) {
      show(button);
    }
  });
  root.addEventListener("mouseout", scheduleHide);
  root.addEventListener("focusin", (event) => {
    if (event.target.matches(".route-help")) {
      show(event.target);
    }
  });
  root.addEventListener("focusout", scheduleHide);
  root.addEventListener("click", (event) => {
    const button = event.target.closest(".route-help");
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      button.focus();
      show(button);
    }
  });
  tooltip.addEventListener("mouseenter", () => clearTimeout(timer));
  tooltip.addEventListener("mouseleave", scheduleHide);
  document.addEventListener("click", (event) => {
    if (!event.target.closest(".route-help") && !tooltip.contains(event.target)) {
      hide();
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      hide();
    }
  });
  window.addEventListener("resize", hide);
  document.addEventListener("scroll", (event) => {
    if (event.target !== tooltip) {
      hide();
    }
  }, true);
  new MutationObserver(() => {
    if (active && !root.contains(active)) {
      hide();
    }
  }).observe(root, { childList: true, subtree: true });
}
